import { useEffect, useState } from "react";
import { api } from "./api";
import { useFeature } from "./features";

// Class-shared data (feature classData): the teacher's panel on Home. Projects save small values for
// the whole class with shareData and read them with getShared. The teacher can look at the keys or wipe them all.

interface SharedItem {
  key: string;
  value: unknown;
  updatedAt: number;
}

export function ClassDataEntry() {
  const on = useFeature("classData");
  const [items, setItems] = useState<SharedItem[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!on) return;
    void api.classDataList().then((r) => setItems(r.items), () => setItems(null));
  }, [on]);

  if (!on || !items) return null;

  const wipe = async () => {
    if (!window.confirm("Wipe all class data? Every project's shared values will be gone.")) return;
    setBusy(true);
    setNote(null);
    try {
      await api.wipeClassData();
      setItems([]);
      setOpen(false);
      setNote("Wiped. Projects start with nothing shared.");
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Class data" className="subs-entry class-data-entry">
      <div>
        <h2>Class data</h2>
        <p className="muted small">
          {items.length === 0
            ? "Nothing shared yet. Projects can save a value for the whole class with shareData."
            : `Class data: ${items.length} key${items.length === 1 ? "" : "s"} · projects read them with getShared`}
        </p>
        {note && (
          <p className="muted small" role="status">
            {note}
          </p>
        )}
      </div>
      {items.length > 0 && (
        <button type="button" className="outline" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? "Hide keys" : "See keys"}
        </button>
      )}
      <button type="button" className="outline" onClick={wipe} disabled={busy || items.length === 0}>
        Wipe
      </button>
      {open && (
        <ul className="class-data-keys">
          {items.map((i) => (
            <li key={i.key}>
              <code>{i.key}</code> {JSON.stringify(i.value)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
